import { useState, useEffect } from 'react';
import api from '../lib/api';

interface Subscription {
  id: string;
  tier: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  agentLimit: number;
}

interface Invoice {
  id: string;
  stripeInvoiceId: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export default function Billing() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState<string>('');

  useEffect(() => {
    fetchSubscription();
    fetchInvoices();
  }, []);

  const fetchSubscription = async () => {
    try {
      const organizationId = 'org-1'; // Replace with actual organization ID
      const response = await api.get(
        `/subscriptions/organization/${organizationId}`,
      );
      setSubscription(response.data);
      setSelectedTier(response.data.tier);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const organizationId = 'org-1'; // Replace with actual organization ID
      const response = await api.get(
        `/subscriptions/invoices/organization/${organizationId}`,
      );
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const handleUpgrade = async () => {
    if (!subscription || selectedTier === subscription.tier) return;

    try {
      await api.patch(`/subscriptions/${subscription.id}`, {
        tier: selectedTier,
      });
      fetchSubscription();
      alert('Subscription updated successfully!');
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Error updating subscription');
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    const confirm = window.confirm(
      'Are you sure you want to cancel your subscription? It will remain active until the end of the current billing period.',
    );

    if (!confirm) return;

    try {
      await api.patch(`/subscriptions/${subscription.id}`, {
        cancelAtPeriodEnd: true,
      });
      fetchSubscription();
      alert('Subscription will be canceled at the end of the billing period.');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Error canceling subscription');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-3xl font-bold mb-8">Billing & Subscription</h1>

      {/* Current Subscription */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">Current Plan</h2>
        {subscription ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plan</p>
                <p className="text-xl font-semibold capitalize">{subscription.tier}</p>
              </div>
              <div
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  subscription.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {subscription.status}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Agent Limit</p>
                <p className="text-lg font-semibold">{subscription.agentLimit}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Next Billing Date</p>
                <p className="text-lg font-semibold">{formatDate(subscription.currentPeriodEnd)}</p>
              </div>
            </div>

            {subscription.cancelAtPeriodEnd && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <p className="text-yellow-800">
                  Your subscription will be canceled on {formatDate(subscription.currentPeriodEnd)}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-600">No active subscription found.</p>
        )}
      </div>

      {/* Upgrade Plans */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">Change Plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {['starter', 'professional', 'enterprise'].map((tier) => (
            <div
              key={tier}
              className={`border rounded-lg p-6 cursor-pointer transition ${
                selectedTier === tier
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300'
              }`}
              onClick={() => setSelectedTier(tier)}
            >
              <h3 className="text-xl font-semibold capitalize mb-2">{tier}</h3>
              <p className="text-3xl font-bold mb-4">
                {tier === 'starter' ? '$49' : tier === 'professional' ? '$149' : '$499'}
                <span className="text-sm text-gray-600">/month</span>
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>Up to {tier === 'starter' ? '10' : tier === 'professional' ? '50' : 'unlimited'} agents</li>
                <li>Advanced forecasting</li>
                <li>Real-time metrics</li>
                {tier !== 'starter' && <li>Priority support</li>}
                {tier === 'enterprise' && <li>Custom integrations</li>}
              </ul>
            </div>
          ))}
        </div>

        {subscription && selectedTier !== subscription.tier && (
          <button
            onClick={handleUpgrade}
            className="mt-6 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
          >
            {selectedTier > subscription.tier ? 'Upgrade' : 'Downgrade'} to {selectedTier}
          </button>
        )}
      </div>

      {/* Billing History */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold mb-4">Billing History</h2>
        {invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Amount</th>
                  <th className="text-left py-3 px-4">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{formatDate(invoice.periodEnd)}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">{formatCurrency(invoice.amountDue, invoice.currency)}</td>
                    <td className="py-3 px-4">
                      {invoice.hostedInvoiceUrl && (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:underline"
                        >
                          View Invoice
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600">No billing history available.</p>
        )}
      </div>

      {/* Cancel Subscription */}
      {subscription && !subscription.cancelAtPeriodEnd && (
        <div className="mt-8">
          <button
            onClick={handleCancelSubscription}
            className="text-red-600 hover:text-red-700 underline text-sm"
          >
            Cancel Subscription
          </button>
        </div>
      )}
    </div>
  );
}
